import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtDate } from "../lib/status";
import { ShieldCheck, CheckCircle2, AlertTriangle, ArrowRight, Building2, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import NoIndex from "../components/NoIndex";

export default function ClaimBusiness() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    owner_name: "", email: "", phone: "", password: "", confirm_password: "",
    role_title: "Owner", business_email: "", business_phone: "", agree_terms: false,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/public/claim/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ not_found: true }))
      .finally(() => setLoading(false));
  }, [token]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm_password) {
      setError("Passwords do not match"); return;
    }
    if (!form.agree_terms) {
      setError("Please agree to the terms"); return;
    }
    setSubmitting(true);
    try {
      const { data: res } = await api.post(`/public/claim/${token}`, {
        owner_name: form.owner_name,
        email: form.email,
        phone: form.phone || undefined,
        password: form.password,
        role_title: form.role_title,
        business_email: form.business_email || undefined,
        business_phone: form.business_phone || undefined,
        agree_terms: form.agree_terms,
      });
      localStorage.setItem("confirmph_token", res.access_token);
      await refresh();
      toast.success(`Welcome, ${res.user.name}! Your business is now active.`);
      nav("/app/requests");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  }
  if (data?.not_found) {
    return (
      <div className="min-h-screen grid place-items-center px-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-700" />
          <h2 className="mt-3 font-display text-2xl font-semibold">Claim link not found</h2>
          <p className="mt-2 text-slate-600 text-sm">This claim link is invalid or has already been used. If you already claimed this business, please log in instead.</p>
          <Link to="/login" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg btn-brand font-medium">Go to login</Link>
        </div>
      </div>
    );
  }
  if (data?.already_claimed) {
    return (
      <div className="min-h-screen grid place-items-center px-4">
        <div className="text-center max-w-md">
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-700" />
          <h2 className="mt-3 font-display text-2xl font-semibold">{data.company?.name} is already claimed</h2>
          <p className="mt-2 text-slate-600 text-sm">A business owner has already claimed this profile. Please log in to your existing account.</p>
          <Link to="/login" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg btn-brand font-medium">Go to login</Link>
        </div>
      </div>
    );
  }

  const c = data.company;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="claim-business-page">
      <NoIndex title="Claim your business · ConfirmPH" />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#1B4332] text-white grid place-items-center">
              <ShieldCheck className="w-4 h-4" strokeWidth={2.2} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-semibold">ConfirmPH</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Claim your business</div>
            </div>
          </Link>
          <Link to="/login" className="text-sm text-slate-600 hover:underline">Already have an account?</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 grid lg:grid-cols-5 gap-8">
        {/* Left: context */}
        <section className="lg:col-span-2 space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs font-medium">
            <Building2 className="w-3.5 h-3.5" /> Unclaimed business profile
          </div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
              Claim <span className="text-[#1B4332]">{c.name}</span> on ConfirmPH
            </h1>
            <p className="mt-3 text-slate-600 leading-relaxed">
              A customer requested booking confirmation from your business through ConfirmPH.
              We've created an unclaimed profile so the request doesn't get lost.
              <span className="block mt-2">This is a neutral verification request: it doesn't imply anything is wrong.</span>
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-900 font-medium">
              <Building2 className="w-4 h-4 text-slate-500" /> {c.name}
            </div>
            {c.city && <div className="flex items-center gap-2 text-slate-600"><MapPin className="w-4 h-4 text-slate-400" /> {c.city}</div>}
            {c.email && <div className="flex items-center gap-2 text-slate-600"><Mail className="w-4 h-4 text-slate-400" /> {c.email}</div>}
            <div className="text-xs text-slate-500 pt-1">Business type: {c.business_type || "-"}</div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Pending verification requests</div>
            <div className="mt-2 text-3xl font-display font-semibold text-slate-900">{data.pending_requests_count}</div>
            <p className="text-xs text-slate-500 mt-1">After claiming, you'll be able to confirm, ask for more info, or mark these as not yours.</p>
            <ul className="mt-3 divide-y divide-slate-100">
              {data.request_previews.slice(0, 3).map((r) => (
                <li key={r.id} className="py-2 text-sm" data-testid={`request-preview-${r.id}`}>
                  <div className="flex justify-between gap-2">
                    <span>{r.customer_name_masked} · {r.vehicle_or_service || "-"}</span>
                    <span className="text-xs text-slate-500">{r.booking_start ? fmtDate(r.booking_start) : ""}</span>
                  </div>
                </li>
              ))}
              {data.pending_requests_count === 0 && (
                <li className="py-2 text-sm text-slate-500">No requests yet.</li>
              )}
            </ul>
          </div>

          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900">
            <h3 className="font-display font-semibold">What is ConfirmPH?</h3>
            <p className="mt-1 leading-relaxed">
              A free booking confirmation layer for Philippine rentals. Keep using Messenger, FB, or walk-ins -
              we just make sure each booking is reviewed and confirmed by your customer via a secure link,
              and warn you before double-bookings happen.
            </p>
          </div>
        </section>

        {/* Right: claim form */}
        <section className="lg:col-span-3">
          <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5" data-testid="claim-form">
            <div>
              <h2 className="font-display text-2xl font-semibold">Create your free business account</h2>
              <p className="text-sm text-slate-600 mt-1">It takes ~30 seconds. Free to start.</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Your name" required testId="claim-owner-name" value={form.owner_name} onChange={set("owner_name")} />
              <F label="Role / title" testId="claim-role" value={form.role_title} onChange={set("role_title")} />
              <F label="Your email" type="email" required testId="claim-email" value={form.email} onChange={set("email")} className="sm:col-span-2" />
              <F label="Your phone" testId="claim-phone" value={form.phone} onChange={set("phone")} />
              <F label="Password (min 6 chars)" type="password" required testId="claim-password" value={form.password} onChange={set("password")} />
              <F label="Confirm password" type="password" required testId="claim-password-confirm" value={form.confirm_password} onChange={set("confirm_password")} className="sm:col-span-2" />

              <div className="sm:col-span-2 border-t border-slate-100 pt-4 mt-2">
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Optional: update business contact</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <F label="Business email" type="email" testId="claim-biz-email" value={form.business_email} onChange={set("business_email")} placeholder={c.email || ""} />
                  <F label="Business phone" testId="claim-biz-phone" value={form.business_phone} onChange={set("business_phone")} placeholder={c.phone || ""} />
                </div>
              </div>

              <label className="sm:col-span-2 flex items-start gap-2 text-sm cursor-pointer mt-1">
                <input type="checkbox" checked={form.agree_terms} onChange={set("agree_terms")} data-testid="claim-agree-terms" className="mt-1" />
                <span>I confirm I'm authorised to claim this business on ConfirmPH and agree to its basic terms of service.</span>
              </label>

              {error && <div className="sm:col-span-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="claim-error">{error}</div>}

              <button
                type="submit"
                disabled={submitting}
                data-testid="claim-submit-btn"
                className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg btn-brand font-medium disabled:opacity-60"
              >
                {submitting ? "Claiming…" : (<>Claim this business <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}

function F({ label, value, onChange, type = "text", required, testId, className = "", placeholder }) {
  return (
    <label className={className}>
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] focus:ring-1 focus:ring-[#2D6A4F] outline-none"
      />
    </label>
  );
}
